/**
 * Activity: when the device was last up, who last used it, and the dates around it. Sits beside the
 * preview in the page header (below it on a phone), so it is the first thing read on every tab.
 */
import type { Control } from 'webwin';
import { ManagementChannel, type DeviceDto } from '../../contracts.gen.js';
import { ago, join, localDate, localDateTime, plural } from '../../format.js';
import type { CrmLabel } from '../../ui/Text.js';
import { card, fillWith, FONT, GAP, splitRow, stackOf, value, vstack } from '../../ui/layout.js';
import type { DeviceTabContext } from './context.js';

export function activityCard(dc: DeviceTabContext): { panel: Control; apply(): void } {
  const lastOnline = factRow('Last online');
  const lastUser = factRow('Last signed in');
  const lastBoot = factRow('Last restart');
  const lastInventory = factRow('Last inventory');
  const primaryUser = factRow('Primary user');
  const enrolled = factRow('Enrolled');
  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      lastOnline.row,
      lastUser.row,
      lastBoot.row,
      lastInventory.row,
      primaryUser.row,
      enrolled.row
    )
  );
  const panel = body;

  /** Called on every presence push as well as on a record change, so it touches only this card. */
  function apply(): void {
    const device = dc.device();
    if (!device) return;
    const phone = device.channel === ManagementChannel.Android;
    const online = dc.isOnline();

    lastOnline.set(
      phone
        ? whenText(device.mobile?.lastStatusReportUtc)
        : online
          ? 'Online now'
          : whenText(lastSeenOf(device)) || 'Never'
    );
    lastOnline.label.text = phone ? 'Last check-in' : 'Last online';

    const signedIn = device.presence?.loggedOnUser;
    if (online && signedIn) {
      lastUser.label.text = 'Signed in';
      lastUser.set(signedIn);
    } else {
      lastUser.label.text = 'Last signed in';
      lastUser.set(
        device.lastLoggedOnUser
          ? join([device.lastLoggedOnUser, whenText(device.lastLoggedOnUtc)])
          : phone ? '' : online ? 'No one signed in' : 'Not recorded yet'
      );
    }

    const boot = device.os?.lastBootUtc;
    lastBoot.set(boot ? join([whenText(boot), online ? `up ${uptime(boot)}` : null]) : '');
    lastInventory.set(whenText(device.lastInventoryUtc));
    primaryUser.set(device.primaryEmployeeName ?? '');
    enrolled.set(localDate(device.enrolledUtc));
  }

  return { panel, apply };
}

/**
 * The later of the live heartbeat and the stored one. The live one is newer while the server has
 * been up; the stored one is all there is after a restart.
 */
export function lastSeenOf(device: DeviceDto): string | null {
  const live = device.presence?.lastSeenUtc ?? null;
  const stored = device.lastSeenUtc ?? null;
  if (!live) return stored;
  if (!stored) return live;
  return Date.parse(live) >= Date.parse(stored) ? live : stored;
}

/** "3 hours ago (Oct 27, 10:12 AM)": the relative time to read, the absolute one to quote. */
function whenText(value: string | null | undefined): string {
  if (!value) return '';
  const relative = ago(value);
  // Past a month `ago` is itself a date, and "9/6/2026 (9/6/2026, 1:46 PM)" says it twice.
  return relative.endsWith('ago') || relative === 'just now' ? `${relative} (${localDateTime(value)})` : localDateTime(value);
}

function uptime(bootUtc: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(bootUtc)) / 60_000));
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return plural(hours, 'hour');
  return plural(Math.floor(hours / 24), 'day');
}

/** A muted label in a fixed column with its value beside it; the row hides when the value is empty. */
function factRow(label: string): { row: Control; label: CrmLabel; set(text: string): void } {
  const name = value(label, { font: FONT.small, role: 'muted', width: 130, autoWidth: false });
  const content = value('', { wrap: true });
  const row = splitRow({ spacing: GAP.xs, align: 'start' });
  row.controls.add(name);
  fillWith(row, content);
  return {
    row,
    label: name,
    set(text: string) {
      content.text = text;
      row.visible = text.length > 0;
    }
  };
}
